/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {MATERIAL_COMPATIBILITY_MODE} from '@angular/material/core';
import {Subscription} from 'rxjs/Subscription';
import {MdPaginatorIntl} from './paginator-intl';
/**
 * The default page size if there is no page size and there are no provided page size options.
 */
const DEFAULT_PAGE_SIZE = 50;
/**
 * Change event object that is emitted when the user selects a
 * different page size or navigates to another page.
 */
export class PageEvent {
/**
 * The current page index.
 */
pageIndex: number;
/**
 * The current page size
 */
pageSize: number;
/**
 * The current total number of items being paged
 */
length: number;
}

function PageEvent_tsickle_Closure_declarations() {
/**
 * The current page index.
 * @type {?}
 */
PageEvent.prototype.pageIndex;
/**
 * The current page size
 * @type {?}
 */
PageEvent.prototype.pageSize;
/**
 * The current total number of items being paged
 * @type {?}
 */
PageEvent.prototype.length;
}

/**
 * Component to provide navigation between paged information. Displays the size of the current
 * page, user-selectable options to change that size, what items are being shown, and
 * navigational button to go to the previous or next page.
 */
export class MdPaginator implements OnInit, OnDestroy {
private _initialized: boolean;
private _intlChanges: Subscription;
/**
 * The zero-based page index of the displayed list of items. Defaulted to 0.
 * @return {?}
 */
get pageIndex(): number { return this._pageIndex; }
/**
 * @param {?} pageIndex
 * @return {?}
 */
set pageIndex(pageIndex: number) {
    this._pageIndex = pageIndex;
    this._changeDetectorRef.markForCheck();
  }
  _pageIndex: number = 0;
/**
 * The length of the total number of items that are being paginated. Defaulted to 0.
 * @return {?}
 */
get length(): number { return this._length; }
/**
 * @param {?} length
 * @return {?}
 */
set length(length: number) {
    this._length = length;
    this._changeDetectorRef.markForCheck();
  }
  _length: number = 0;
/**
 * Number of items to display on a page. By default set to 50.
 * @return {?}
 */
get pageSize(): number { return this._pageSize; }
/**
 * @param {?} pageSize
 * @return {?}
 */
set pageSize(pageSize: number) {
    this._pageSize = pageSize;
    this._updateDisplayedPageSizeOptions();
  }
private _pageSize: number;
/**
 * The set of provided page size options to display to the user.
 * @return {?}
 */
get pageSizeOptions(): number[] { return this._pageSizeOptions; }
/**
 * @param {?} pageSizeOptions
 * @return {?}
 */
set pageSizeOptions(pageSizeOptions: number[]) {
    this._pageSizeOptions = pageSizeOptions;
    this._updateDisplayedPageSizeOptions();
  }
private _pageSizeOptions: number[] = [];
/**
 * Event emitted when the paginator changes the page size or page index.
 */
page = new EventEmitter<PageEvent>();
/**
 * Displayed set of page size options. Will be sorted and include current page size.
 */
_displayedPageSizeOptions: number[];
/**
 * @param {?} _intl
 * @param {?} _changeDetectorRef
 */
constructor(public _intl: MdPaginatorIntl,
private _changeDetectorRef: ChangeDetectorRef) {
    this._intlChanges = _intl.changes.subscribe(() => this._changeDetectorRef.markForCheck());
  }
/**
 * @return {?}
 */
ngOnInit() {
    this._initialized = true;
    this._updateDisplayedPageSizeOptions();
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._intlChanges.unsubscribe();
  }
/**
 * Advances to the next page if it exists.
 * @return {?}
 */
nextPage() {
    if (!this.hasNextPage()) { return; }
    this.pageIndex++;
    this._emitPageEvent();
  }
/**
 * Move back to the previous page if it exists.
 * @return {?}
 */
previousPage() {
    if (!this.hasPreviousPage()) { return; }
    this.pageIndex--;
    this._emitPageEvent();
  }
/**
 * Whether there is a previous page.
 * @return {?}
 */
hasPreviousPage() {
    return this.pageIndex >= 1 && this.pageSize != 0;
  }
/**
 * Whether there is a next page.
 * @return {?}
 */
hasNextPage() {
    const /** @type {?} */ numberOfPages = Math.ceil(this.length / this.pageSize) - 1;
    return this.pageIndex < numberOfPages && this.pageSize != 0;
  }
/**
 * Changes the page size so that the first item displayed on the page will still be
 * displayed using the new page size.
 * 
 * For example, if the page size is 10 and on the second page (items indexed 10-19) then
 * switching so that the page size is 5 will set the third page as the current page so
 * that the 10th item will still be displayed.
 * @param {?} pageSize
 * @return {?}
 */
_changePageSize(pageSize: number) {
    // Current page needs to be updated to reflect the new page size. Navigate to the page
    // containing the previous page's first item.
    const /** @type {?} */ startIndex = this.pageIndex * this.pageSize;
    this.pageIndex = Math.floor(startIndex / pageSize) || 0;

    this.pageSize = pageSize;
    this._emitPageEvent();
  }
/**
 * Updates the list of page size options to display to the user. Includes making sure that
 * the page size is an option and that the list is sorted.
 * @return {?}
 */
private _updateDisplayedPageSizeOptions() {
    if (!this._initialized) { return; }

    // If no page size is provided, use the first page size option or the default page size.
    if (!this.pageSize) {
      this._pageSize = this.pageSizeOptions.length != 0 ?
          this.pageSizeOptions[0] :
          DEFAULT_PAGE_SIZE;
    }

    this._displayedPageSizeOptions = this.pageSizeOptions.slice();
    if (this._displayedPageSizeOptions.indexOf(this.pageSize) == -1) {
      this._displayedPageSizeOptions.push(this.pageSize);
    }

    // Sort the numbers using a number-specific sort function.
    this._displayedPageSizeOptions.sort((a, b) => a - b);

    this._changeDetectorRef.markForCheck();
  }
/**
 * Emits an event notifying that a change of the paginator's properties has been triggered.
 * @return {?}
 */
private _emitPageEvent() {
    this.page.next({
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      length: this.length
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-paginator, mat-paginator',
  templateUrl: 'paginator.html',
  styleUrls: ['paginator.css'],
  host: {
    'class': 'mat-paginator',
  },
  viewProviders: [{provide: MATERIAL_COMPATIBILITY_MODE, useValue: true}],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdPaginatorIntl, },
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'pageIndex': [{ type: Input },],
'length': [{ type: Input },],
'pageSize': [{ type: Input },],
'pageSizeOptions': [{ type: Input },],
'page': [{ type: Output },],
};
}

function MdPaginator_tsickle_Closure_declarations() {
/** @type {?} */
MdPaginator.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdPaginator.ctorParameters;
/** @type {?} */
MdPaginator.propDecorators;
/** @type {?} */
MdPaginator.prototype._initialized;
/** @type {?} */
MdPaginator.prototype._intlChanges;
/** @type {?} */
MdPaginator.prototype._pageIndex;
/** @type {?} */
MdPaginator.prototype._length;
/** @type {?} */
MdPaginator.prototype._pageSize;
/** @type {?} */
MdPaginator.prototype._pageSizeOptions;
/**
 * Event emitted when the paginator changes the page size or page index.
 * @type {?}
 */
MdPaginator.prototype.page;
/**
 * Displayed set of page size options. Will be sorted and include current page size.
 * @type {?}
 */
MdPaginator.prototype._displayedPageSizeOptions;
/** @type {?} */
MdPaginator.prototype._intl;
/** @type {?} */
MdPaginator.prototype._changeDetectorRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
